import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Form, Input, Button, Checkbox, App } from "antd";

import { setUserSession } from "../../../services/auth";
import { login } from "../api/LoginAPI";
import { styles } from "../styles/loginStyles";
import { ILogin, LoginSchema } from "../type/loginType";

export default function LoginForm() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ILogin>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      rememberMe: false,
    },
  });

  const onSubmit = async (data: ILogin) => {
    setIsSubmitting(true);
    try {
      const userDetail = await login(data);
      const setUser = setUserSession({
        rememberMe: data.rememberMe,
        token: userDetail.tokens,
        user: userDetail.user,
      });
      setUser === true ? navigate(0) : message.error("error");
    } catch (error: any) {
      message.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      name="loginForm"
      initialValues={{ rememberMe: false }}
      onFinish={handleSubmit(onSubmit)}
      labelAlign="left"
      colon={false}
      layout="vertical"
    >
      <Form.Item
        name="email"
        hasFeedback
        validateStatus={errors.email ? "error" : ""}
        help={errors.email?.message}
      >
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input placeholder="Email" {...field} size="large" />
          )}
        />
      </Form.Item>

      <Form.Item
        name="password"
        hasFeedback
        validateStatus={errors.password ? "error" : ""}
        help={errors.password?.message}
        style={{ marginBottom: 10 }}
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input.Password placeholder="Password" {...field} size="large" />
          )}
        />
      </Form.Item>

      <Form.Item
        name="rememberMe"
        hasFeedback
        validateStatus={errors.rememberMe ? "error" : ""}
        help={errors.rememberMe?.message}
      >
        <Controller
          name="rememberMe"
          control={control}
          render={({ field }) => (
            <Checkbox checked={field.value} {...field}>
              Remember Me?
            </Checkbox>
          )}
        />
      </Form.Item>

      <Form.Item>
        <Button
          style={styles.loginButton}
          type="primary"
          htmlType="submit"
          size="large"
          loading={isSubmitting}
        >
          Login
        </Button>
        <div style={styles.forgotPassword}>
          <a href="/forgot-password">Forgot Password</a>
        </div>
      </Form.Item>
    </Form>
  );
}
